import { Controller } from "@hotwired/stimulus"
import { ErrorHandler } from "./error_handler"
import { ImageProcessor } from "./image_processor"
import { CropManager } from "./crop_manager"
import { DomManager } from "./dom_manager"
import { FileManager } from "./file_manager"

/*
 * BaseCropperController
 *
 * Abstract base class for image cropping Stimulus controllers. Provides
 * common functionality for image upload, pre-processing, cropping, and
 * preview management.
 *
 * Subclasses must implement:
 *   - static imageTypes: Array of image type identifiers
 *   - static imageConfig: Configuration object for each image type
 *   - getTargetsForType(type): Returns target elements for a type
 *
 * Configuration per image type:
 *   - maxWidth: Maximum width after pre-processing
 *   - maxHeight: Maximum height after pre-processing
 *   - aspectRatio: Aspect ratio for cropping (NaN = free)
 *   - format: Output format ("png" or "jpeg")
 *   - quality: JPEG quality (0-1), default 0.9
 *   - backgroundColor: Padding background ("transparent" or "white")
 */
export class BaseCropperController extends Controller {
  /*
   * connect
   *
   * Stimulus lifecycle callback. Initialises shared utilities, reads
   * crop data from DOM, restores previews, and sets up form handling.
   */
  connect() {
    this._initializeUtilities()
    this._readConfigFromDom()
    this._initializeState()
    this._readAllCropData()
    this._restoreAllPreviews()
    this._setupFormSubmitHandler()
  }

  /*
   * disconnect
   *
   * Stimulus lifecycle callback. Cleans up resources.
   */
  disconnect() {
    this.cropManager.destroy()
    this.domManager.revokeAllBlobUrls()
  }

  /*
   * _initializeUtilities
   *
   * Creates instances of shared utility classes.
   */
  _initializeUtilities() {
    const controllerName = this.constructor.name || "ImageCropper"
    this.errorHandler = new ErrorHandler(controllerName)
    this.imageProcessor = new ImageProcessor(this.errorHandler)
    this.cropManager = new CropManager(this.errorHandler, this.imageProcessor)
    this.domManager = new DomManager(this.errorHandler)
    this.fileManager = new FileManager(
      this.errorHandler,
      this.imageProcessor,
      this.domManager
    )
  }

  /*
   * _initializeState
   *
   * Initialises controller state.
   */
  _initializeState() {
    this.cropData = {}
    this._processingFiles = false
  }

  /*
   * _getConfig
   *
   * Gets the configuration for an image type with defaults applied.
   *
   * Input:
   *   type {string} - Image type identifier.
   *
   * Output:
   *   {object} - Configuration object with defaults.
   */
  _getConfig(type) {
    const config = this.constructor.imageConfig[type] || {}
    return {
      maxWidth: config.maxWidth || 2000,
      maxHeight: config.maxHeight || 2000,
      aspectRatio: config.aspectRatio ?? NaN,
      format: config.format || "png",
      quality: config.quality ?? 0.9,
      backgroundColor: config.backgroundColor || "transparent",
      ...config
    }
  }

  /*
   * getTargetsForType
   *
   * Returns an object containing all target elements for a given image type.
   * Must be implemented by subclasses.
   *
   * Input:
   *   type {string} - Image type identifier.
   *
   * Output:
   *   {object} - Object with target elements (preview, cropButton, modal, etc.)
   */
  getTargetsForType(type) { // eslint-disable-line no-unused-vars
    throw new Error("getTargetsForType must be implemented by subclass")
  }

  /*
   * _readAllCropData
   *
   * Reads crop data from DOM for all image types.
   */
  _readAllCropData() {
    this.constructor.imageTypes.forEach((type) => {
      const targets = this.getTargetsForType(type)
      this.cropData[type] = this.domManager.parseJsonField(targets.cropData)
    })
  }

  /*
   * getCropData
   *
   * Gets stored crop data for an image type.
   *
   * Input:
   *   type {string} - Image type identifier.
   *
   * Output:
   *   {object|null} - Crop data or null.
   */
  getCropData(type) {
    return this.cropData[type] || null
  }

  /*
   * setCropData
   *
   * Stores crop data for an image type in memory and DOM.
   *
   * Inputs:
   *   type {string}      - Image type identifier.
   *   data {object|null} - Crop data to store.
   */
  setCropData(type, data) {
    this.cropData[type] = data
    const targets = this.getTargetsForType(type)
    this.domManager.setJsonField(targets.cropData, data)
  }

  /*
   * _restoreAllPreviews
   *
   * Restores preview images for all types from stored data URLs.
   */
  _restoreAllPreviews() {
    this.constructor.imageTypes.forEach((type) => {
      this._restorePreviewForType(type)
    })
  }

  /*
   * _restorePreviewForType
   *
   * Restores preview for a single image type from stored data URLs.
   *
   * Input:
   *   type {string} - Image type identifier.
   */
  async _restorePreviewForType(type) {
    const targets = this.getTargetsForType(type)
    const config = this._getConfig(type)

    const originalDataUrl = this.domManager.getHiddenFieldValue(targets.originalData)
    const previewDataUrl = this.domManager.getHiddenFieldValue(targets.previewData)
    const sourceUrl = this._getSourceUrl(type)

    const cropData = this.getCropData(type)
    const hasStoredDataUrls = originalDataUrl || previewDataUrl

    const dataUrl = originalDataUrl || previewDataUrl || sourceUrl

    if (!dataUrl || !this.fileManager.isValidImageUrl(dataUrl)) return

    try {
      const previewAlreadyServer = this._previewAlreadyShowsServerImage(targets.preview)

      if (cropData && cropData.width > 0 && cropData.height > 0) {
        const croppedBlob = await this.imageProcessor.applyCrop(
          dataUrl,
          cropData,
          config.format,
          config.quality
        )
        const croppedDataUrl = await this.imageProcessor.blobToDataUrl(croppedBlob)
        this.domManager.updatePreview(targets.preview, croppedDataUrl)
      } else if (hasStoredDataUrls) {
        this.domManager.updatePreview(targets.preview, dataUrl)
      } else if (!previewAlreadyServer) {
        this.domManager.updatePreview(targets.preview, dataUrl)
      }

      this.domManager.showElement(targets.cropButton)
      const removeBtn = this.element.querySelector(`button[data-remove-button-for="${type}"]`)
      if (removeBtn) this.domManager.showElement(removeBtn)

      if (originalDataUrl) {
        const blob = await this.fileManager.fetchBlobFromDataUrl(originalDataUrl)
        const blobUrl = URL.createObjectURL(blob)
        this.domManager.storeBlobUrl(type, blobUrl)
      }
    } catch (error) {
      this.errorHandler.handle(
        `RESTORE_PREVIEW_${type.toUpperCase()}`,
        `Failed to restore preview for ${type}`,
        error
      )
    }
  }

  /*
   * _previewAlreadyShowsServerImage
   *
   * Returns true if the preview img already has a server-rendered src (e.g. cropped
   * attachment URL). Used to avoid overwriting the correct preview on page load when
   * we have no client-side crop data to re-apply.
   */
  _previewAlreadyShowsServerImage(previewEl) {
    if (!previewEl?.src) return false
    const s = previewEl.src
    if (!s || s === "#" || s.startsWith("data:")) return false
    return s.startsWith("http:") || s.startsWith("https:") || s.startsWith("/")
  }

  /*
   * _getSourceUrl
   *
   * Gets the source URL for an image type from various possible sources.
   * Can be overridden by subclasses for custom source URL handling.
   *
   * Input:
   *   type {string} - Image type identifier.
   *
   * Output:
   *   {string} - Source URL or empty string.
   */
  _getSourceUrl(type) {
    const targets = this.getTargetsForType(type)
    return (
      this.domManager.getDataAttribute(targets.sourceUrl, "sourceUrl") ||
      this.domManager.getHiddenFieldValue(targets.originalUrl) ||
      ""
    )
  }

  /*
   * fileSelected
   *
   * Stimulus action handler for file input change events. Processes the
   * selected file and updates previews.
   *
   * Input:
   *   event {Event} - The change event from the file input.
   */
  async fileSelected(event) {
    if (this._processingFiles) return

    const input = event.currentTarget
    const type = input.dataset.type

    if (!input.files || !input.files[0]) return

    const file = input.files[0]
    const targets = this.getTargetsForType(type)
    const config = this._getConfig(type)

    this.domManager.hideElement(targets.cropButton)

    try {
      this._processingFiles = true
      await this._processUploadedFile(type, file, targets, config)
    } catch (error) {
      this.errorHandler.handle(
        `FILE_PROCESS_${type.toUpperCase()}`,
        `Failed to process image for ${type}`,
        error,
        true
      )
      this.domManager.showElement(targets.cropButton)
    } finally {
      this._processingFiles = false
    }
  }

  /*
   * _processUploadedFile
   *
   * Processes an uploaded file: resize, pad, compute default crop, and update DOM.
   *
   * Inputs:
   *   type    {string} - Image type identifier.
   *   file    {File}   - The uploaded file.
   *   targets {object} - Target elements for this type.
   *   config  {object} - Configuration for this type.
   */
  async _processUploadedFile(type, file, targets, config) {
    // Step 1: Resize to fit within max dimensions
    let processedBlob = await this.imageProcessor.containWithinMax(
      file,
      config.maxWidth,
      config.maxHeight,
      config.format,
      config.quality
    )

    // Step 2: Pad to aspect ratio if needed
    processedBlob = await this.imageProcessor.padToAspectRatio(
      processedBlob,
      config.aspectRatio,
      config.backgroundColor,
      config.format,
      config.quality
    )

    // Step 3: Convert to final format
    if (config.format === "jpeg") {
      processedBlob = await this.imageProcessor.convertFormat(
        processedBlob,
        "jpeg",
        config.quality,
        "white"
      )
    }

    // Step 4: Get dimensions and calculate default crop
    const fullDataUrl = await this.imageProcessor.blobToDataUrl(processedBlob)
    const { width, height } = await this.imageProcessor.getImageDimensions(fullDataUrl)
    const defaultCrop = this.imageProcessor.calculateDefaultCrop(width, height, config.aspectRatio)

    // Step 5: Apply default crop
    const croppedBlob = await this.imageProcessor.applyCrop(
      fullDataUrl,
      defaultCrop,
      config.format,
      config.quality
    )
    const croppedDataUrl = await this.imageProcessor.blobToDataUrl(croppedBlob)

    // Step 6: Update DOM
    await this._updateDomAfterProcessing(type, {
      processedBlob,
      fullDataUrl,
      croppedBlob,
      croppedDataUrl,
      cropData: defaultCrop,
      // originalFile: file,
      targets,
      config
    })
  }

  /*
   * _updateDomAfterProcessing
   *
   * Updates all DOM elements after image processing.
   *
   * Input:
   *   type {string} - Image type identifier.
   *   data {object} - Processing results containing blobs, data URLs, etc.
   */
  async _updateDomAfterProcessing(type, data) {
    const {
      processedBlob,
      fullDataUrl,
      croppedBlob,
      croppedDataUrl,
      cropData,
      // originalFile,
      targets,
      config
    } = data

    // Store blob URL for modal
    const blobUrl = URL.createObjectURL(processedBlob)
    this.domManager.storeBlobUrl(type, blobUrl)

    // Update preview
    this.domManager.updatePreview(targets.preview, croppedDataUrl)
    this.domManager.showElement(targets.cropButton)
    const removeBtn = this.element.querySelector(`button[data-remove-button-for="${type}"]`)
    if (removeBtn) this.domManager.showElement(removeBtn)

    // Store data URLs in hidden fields
    this.domManager.setHiddenFieldValue(targets.originalData, fullDataUrl)
    this.domManager.setHiddenFieldValue(targets.previewData, croppedDataUrl)

    // Clear server URL since we have a new upload
    if (targets.originalUrl) {
      this.domManager.setHiddenFieldValue(targets.originalUrl, "")
    }

    // Store crop data
    this.setCropData(type, cropData)

    // Create and set file inputs
    const mimeType = this.fileManager.getMimeType(config.format)
    const ext = this.fileManager.getFileExtension(config.format)

    const originalFile2 = this.fileManager.createFile(
      processedBlob,
      `source_${type}${ext}`,
      mimeType
    )

    const croppedFile = this.fileManager.createFile(
      croppedBlob,
      `cropped_${type}${ext}`,
      mimeType
    )

    if (targets.sourceInput) {
      this.domManager.setFileInput(targets.sourceInput, originalFile2)
    }
    if (targets.croppedInput) {
      this.domManager.setFileInput(targets.croppedInput, croppedFile)
    }
    const removeFlag = this.element.querySelector(`input[name$="[_remove_${type}]"]`)
    if (removeFlag) removeFlag.value = ""

    // Dispatch update event
    this._dispatchUpdateEvent(type)
  }

  /*
   * _dispatchUpdateEvent
   *
   * Dispatches a custom event when an image is updated.
   *
   * Input:
   *   type {string} - Image type identifier.
   */
  _dispatchUpdateEvent(type) {
    this.element.dispatchEvent(
      new CustomEvent("image-cropper-update", {
        bubbles: true,
        detail: { type }
      })
    )
  }

  /*
   * openCropModal
   *
   * Stimulus action handler for opening the crop modal.
   *
   * Input:
   *   event {Event} - Click event from the crop button.
   */
  openCropModal(event) {
    event.preventDefault()

    const button = event.currentTarget
    const type = button.dataset.type
    const targets = this.getTargetsForType(type)
    const config = this._getConfig(type)

    // Determine image source
    const blobUrl = this.domManager.getBlobUrl(type)
    const originalDataUrl = this.domManager.getHiddenFieldValue(targets.originalData)
    const sourceUrl = this._getSourceUrl(type)

    const imageSrc = blobUrl ||
      (this.fileManager.isValidImageUrl(originalDataUrl) ? originalDataUrl : null) ||
      (this.fileManager.isValidImageUrl(sourceUrl) ? sourceUrl : null)

    if (!imageSrc) {
      this.errorHandler.handle(
        "CROP_SOURCE_MISSING",
        `No source image available for ${type}`,
        null,
        true
      )
      return
    }

    // Set modal image
    targets.modalImage.src = imageSrc
    targets.modalImage.onerror = () => {
      this.errorHandler.handle(
        "MODAL_IMG_LOAD",
        `Failed to load image in crop modal for ${type}`,
        null,
        true
      )
    }

    // Open modal and start cropper (backdrop: static so releasing drag outside doesn't close it)
    const $modal = window.jQuery(targets.modal)
    $modal.one("shown.bs.modal", () => {
      if (targets.modalImage.complete && targets.modalImage.naturalWidth > 0) {
        this._startCropperForType(type, targets, config)
      } else {
        targets.modalImage.onload = () => this._startCropperForType(type, targets, config)
      }
    })
    $modal.one("hidden.bs.modal", () => this.cropManager.destroy())
    $modal.modal({ backdrop: "static", show: true })
  }

  /*
   * _startCropperForType
   *
   * Starts the cropper for a specific image type.
   *
   * Inputs:
   *   type    {string} - Image type identifier.
   *   targets {object} - Target elements.
   *   config  {object} - Configuration.
   */
  _startCropperForType(type, targets, config) {
    this.cropManager.startCropper(targets.modalImage, {
      aspectRatio: config.aspectRatio,
      initialCrop: this.getCropData(type),
      type
    })
  }

  /*
   * applyCrop
   *
   * Stimulus action handler for applying a crop from the modal.
   *
   * Input:
   *   event {Event} - Click event from the apply button.
   */
  async applyCrop(event) {
    if (!this.cropManager.isActive()) return

    const type = event.currentTarget.dataset.type
    const targets = this.getTargetsForType(type)
    const config = this._getConfig(type)

    try {
      const cropData = this.cropManager.getCropData()
      const croppedCanvas = this.cropManager.getCroppedCanvas()

      if (!croppedCanvas) {
        throw new Error("getCroppedCanvas returned null")
      }

      // Store crop data
      this.setCropData(type, cropData)

      // Get cropped image as blob
      const croppedBlob = await new Promise((resolve, reject) => {
        const mimeType = this.fileManager.getMimeType(config.format)
        croppedCanvas.toBlob(
          (blob) => {
            if (blob) resolve(blob)
            else reject(new Error("Canvas toBlob returned null"))
          },
          mimeType,
          config.quality
        )
      })

      // Convert to data URL for preview
      const croppedDataUrl = await this.imageProcessor.blobToDataUrl(croppedBlob)

      // Update preview
      this.domManager.updatePreview(targets.preview, croppedDataUrl)

      // Store preview data URL
      this.domManager.setHiddenFieldValue(targets.previewData, croppedDataUrl)

      // Update cropped file input
      if (targets.croppedInput) {
        const ext = this.fileManager.getFileExtension(config.format)
        const mimeType = this.fileManager.getMimeType(config.format)
        const croppedFile = this.fileManager.createFile(
          croppedBlob,
          `cropped_${type}${ext}`,
          mimeType
        )
        this.domManager.setFileInput(targets.croppedInput, croppedFile)
      }

      // Dispatch update event
      this._dispatchUpdateEvent(type)

      // Close modal
      this.closeModal(event)
    } catch (error) {
      this.errorHandler.handle(
        `APPLY_CROP_${type.toUpperCase()}`,
        `Failed to apply crop for ${type}`,
        error,
        true
      )
    }
  }

  /*
   * closeModal
   *
   * Stimulus action handler for closing the crop modal.
   *
   * Input:
   *   event {Event} - Click event.
   */
  closeModal(event) {
    const type = event?.currentTarget?.dataset?.type || this.cropManager.getActiveType()
    if (!type) return

    const targets = this.getTargetsForType(type)
    this.cropManager.destroy()
    window.jQuery(targets.modal).modal("hide")
  }

  /*
   * removeImage
   *
   * Stimulus action handler for removing an optional image (e.g. logo).
   * Clears file inputs, hidden data, preview, and dispatches image-cropper-update.
   *
   * Input:
   *   event {Event} - Click event; event.currentTarget.dataset.type must be the image type.
   */
  removeImage(event) {
    event.preventDefault()
    const type = event.currentTarget?.dataset?.type
    if (!type || !this.constructor.imageTypes.includes(type)) return

    const targets = this.getTargetsForType(type)
    if (targets.sourceInput) targets.sourceInput.value = ""
    if (targets.croppedInput) targets.croppedInput.value = ""
    this.domManager.setHiddenFieldValue(targets.previewData, "")
    this.domManager.setHiddenFieldValue(targets.originalData, "")
    this.setCropData(type, null)
    this.domManager.updatePreview(targets.preview, "#")
    this.domManager.hideElement(targets.preview)
    this.domManager.hideElement(targets.cropButton)
    const removeBtn = this.element.querySelector(`button[data-remove-button-for="${type}"]`)
    if (removeBtn) this.domManager.hideElement(removeBtn)
    const removeFlag = this.element.querySelector(`input[name$="[_remove_${type}]"]`)
    if (removeFlag) removeFlag.value = "1"
    this.domManager.revokeBlobUrl(type)
    this._dispatchUpdateEvent(type)
  }

  /*
   * _setupFormSubmitHandler
   *
   * Sets up form submission handling to restore files if needed.
   */
  _setupFormSubmitHandler() {
    const form = this.element.closest("form")
    if (!form) return

    form.addEventListener("submit", async (event) => {
      if (this._needsFileRestore()) {
        event.preventDefault()
        event.stopPropagation()

        try {
          await this._restoreAllFiles()
          this._clearDataUrlFields()
          form.submit()
        } catch (error) {
          this.errorHandler.handle(
            "SUBMIT_RESTORE",
            "Failed to restore files before submission",
            error,
            true
          )
          this._clearDataUrlFields()
          form.submit()
        }
      } else {
        this._clearDataUrlFields()
      }
    })
  }

  /*
   * _needsFileRestore
   *
   * Checks if any image type needs file restoration before submission.
   *
   * Output:
   *   {boolean} - True if restoration is needed.
   */
  _needsFileRestore() {
    return this.constructor.imageTypes.some((type) => {
      const targets = this.getTargetsForType(type)
      const hasData = this.domManager.getHiddenFieldValue(targets.originalData)
      if (!hasData) return false

      const hasSourceFile = this.domManager.hasFiles(targets.sourceInput)
      const hasCroppedFile = this.domManager.hasFiles(targets.croppedInput)

      return !hasSourceFile || !hasCroppedFile
    })
  }

  /*
   * _restoreAllFiles
   *
   * Restores files for all image types from data URLs.
   */
  async _restoreAllFiles() {
    const results = await Promise.allSettled(
      this.constructor.imageTypes.map((type) => this._restoreFilesForType(type))
    )

    const failures = results.filter((r) => r.status === "rejected")
    if (failures.length > 0) {
      this.errorHandler.handle(
        "RESTORE_PARTIAL",
        `${failures.length} image type(s) failed to restore`
      )
    }
  }

  /*
   * _restoreFilesForType
   *
   * Restores files for a single image type from data URLs.
   *
   * Input:
   *   type {string} - Image type identifier.
   */
  async _restoreFilesForType(type) {
    const targets = this.getTargetsForType(type)
    const config = this._getConfig(type)

    const originalDataUrl = this.domManager.getHiddenFieldValue(targets.originalData)
    const previewDataUrl = this.domManager.getHiddenFieldValue(targets.previewData)

    if (!originalDataUrl) return

    const mimeType = this.fileManager.getMimeType(config.format)
    const ext = this.fileManager.getFileExtension(config.format)

    // Restore source file
    if (targets.sourceInput && !this.domManager.hasFiles(targets.sourceInput)) {
      await this.fileManager.restoreFileFromDataUrl(
        originalDataUrl,
        targets.sourceInput,
        `source_${type}${ext}`,
        mimeType
      )
    }

    // Restore cropped file
    if (targets.croppedInput && !this.domManager.hasFiles(targets.croppedInput)) {
      const dataUrl = previewDataUrl || originalDataUrl
      await this.fileManager.restoreFileFromDataUrl(
        dataUrl,
        targets.croppedInput,
        `cropped_${type}${ext}`,
        mimeType
      )
    }
  }

  /*
   * _clearDataUrlFields
   *
   * Clears data URL hidden fields before form submission.
   */
  _clearDataUrlFields() {
    this.constructor.imageTypes.forEach((type) => {
      const targets = this.getTargetsForType(type)
      this.domManager.setHiddenFieldValue(targets.originalData, "")
      this.domManager.setHiddenFieldValue(targets.previewData, "")
    })
  }

  /*
   * _readConfigFromDom
   *
   * Reads croppable image configuration from a data attribute injected
   * by the Rails CroppableConfigComponent. If found, merges it with
   * the static imageConfig on the controller class.
   *
   * This allows Rails models to be the single source of truth for
   * crop settings like aspect ratio, max dimensions, and format.
   */
  _readConfigFromDom() {
    const configEl = this.element.querySelector("[data-croppable-config]")
    if (!configEl) return

    try {
      const serverConfig = JSON.parse(configEl.dataset.croppableConfig)
      if (serverConfig && typeof serverConfig === "object") {
        Object.entries(serverConfig).forEach(([type, config]) => {
          if (!this.constructor.imageConfig[type]) {
            this.constructor.imageConfig[type] = {}
          }
          Object.assign(this.constructor.imageConfig[type], config)
        })
      }
    } catch (error) {
      this.errorHandler?.handle("CONFIG_PARSE", "Failed to parse server config", error)
    }
  }
}
